const { flex } = window;

import {
  USA_POSTAL_CODE_VALIDATION,
  CANADIAN_POSTAL_CODE_VALIDATION,
  CONTACT_KINDS,
  CONFIG_CODES,
  NO_WHITE_SPACE_CODE_VALIDATION,
  CHECK_SPECIAL_WORDS_CODE_VALIDATION,
  VALIDATION_ERROR_MESSAGES,
  CHECK_CHARACTER_VALIDATION,
  CHECK_NOT_START_SPECIAL_CHARACTER_VALIDATION,
  EMAIL_VALIDATION,
  DEFAULT_PHONE_COUNTRY,
  PHONE_VALIDATION,
} from '../constants';
import {
  CREATE_MANHEIM_ACCOUNT,
  PEOPLE_CREATEUSER_MODAL_MESSAGES,
} from '../../../public/js/constants/resources';
import { USER_NAME_LENGTH } from '../../../shared/js/constants';
import { INVALID_PASSWORD_CANNOT_CONTAIN_SPACE_TEXT } from '../../../../src/public/js/constants/resources';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { FEATURE } from '../../../public/js/constants/features';

export const getMinLengthValidator = (field, fieldName, minLength) => {
  minLength = minLength || 1;

  return field
    ? {
        minLen: (model) =>
          model[field] && model[field] !== '' ? model[field].trim().length >= minLength : true,
        message:
          field === 'username'
            ? flex.formatString(
                CREATE_MANHEIM_ACCOUNT.CHECK_LENGTH_SPACE_WORDS_MSG,
                fieldName,
                minLength,
                USER_NAME_LENGTH.MAX
              )
            : VALIDATION_ERROR_MESSAGES.MIN_LENGTH(fieldName || field, minLength),
      }
    : { minLen: null, message: '' };
};

export const getMaxLengthValidator = (field, fieldName, maxLength) => {
  maxLength = maxLength || 20;

  return field
    ? {
        maxLen: (model) =>
          model[field] && model[field].trim() !== ''
            ? model[field].trim().length <= maxLength
            : true,
        message: flex.formatString(
          CREATE_MANHEIM_ACCOUNT.CHECK_LENGTH_SPACE_WORDS_MSG,
          fieldName,
          USER_NAME_LENGTH.MIN,
          maxLength
        ),
      }
    : { maxLen: null, message: '' };
};

export const getUniqueValueValidator = (field, fieldName, sourceArray, isCaseInsensitive) => {
  sourceArray = sourceArray || [];

  return field
    ? {
        unique: (model) => {
          let value = model[field];
          const isString = value && typeof value === 'string';

          if (isString && isCaseInsensitive) {
            sourceArray = sourceArray.map((val) => val.toLowerCase());
            value = value.toLowerCase();
          }

          return value && (!isString || value.trim() !== '') ? !sourceArray.includes(value) : true;
        },
        message: VALIDATION_ERROR_MESSAGES.UNIQUE(fieldName || field),
      }
    : { unique: null, message: '' };
};

export const getRegexpValidator = (field, fieldName, regexp, symbols) => {
  return field
    ? {
        regexp: (model) =>
          model[field] && model[field] !== '' ? model[field].match(regexp) : true,
        message: VALIDATION_ERROR_MESSAGES.MATCH_REGEXP(fieldName || field, symbols || []),
      }
    : { regexp: null, message: '' };
};

export const getContactValidationAndMessage = (kind) => {
  switch (kind) {
    case CONTACT_KINDS.EMAIL:
      return { validations: 'isEmail', message: 'This is not a valid email' };
    case CONTACT_KINDS.PHONE:
    case CONTACT_KINDS.FAX: {
      const isAMSOrchestrationEnabled = flex.isFeatureToggleEnabled(
        FEATURE.ENABLE_AMS_USER_ORCHESTRATION
      );

      if (!isAMSOrchestrationEnabled) {
        return {
          validations: { matchRegexp: /^[0-9]{3}[-][0-9]{3}[-][0-9]{4}$/ },
          validationErrors: { matchRegexp: CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_NAT_NUMBER_ERR },
          message: CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_NAT_NUMBER_ERR
        };
      }

      const phoneValidation = getPhoneValidation("value", true);

      return {
        ...phoneValidation,
        message: CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_INT_NUMBER_ERR,
      };
    }
    default:
      return { validation: null, message: `${kind} is required` };
  }
};

export const getParsedContactValue = (kind, value) => {
  switch (kind) {
    case CONTACT_KINDS.PHONE:
    case CONTACT_KINDS.FAX:
      return parsePhone(value);
    default:
      return value;
  }
};

export const getPostalCodeValidation = (regionCode) => {
  const usaCodes = flex.getConfigCodes(CONFIG_CODES.UNITED_STATES);
  const usaCode = usaCodes.find(({ code }) => code === regionCode);

  if (usaCode) {
    return {
      regexp: { matchRegexp: USA_POSTAL_CODE_VALIDATION.REG_EX },
      message: USA_POSTAL_CODE_VALIDATION.MESSAGE,
    };
  }

  const canadianCodes = flex.getConfigCodes(CONFIG_CODES.CANADIAN_PROVINCES);
  const canadianCode = canadianCodes.find(({ code }) => code === regionCode);

  if (canadianCode) {
    return {
      regexp: { matchRegexp: CANADIAN_POSTAL_CODE_VALIDATION.REG_EX },
      message: CANADIAN_POSTAL_CODE_VALIDATION.MESSAGE,
    };
  }

  return { regexp: null, message: '' };
};

export const getUsernameValidation = (fieldName = 'username') => {
  return {
    validators: {
      stringLength: (model) => {
        const value = model[fieldName];
        return !value || isValidUsernameLength(value);
      },
      stringLengthAndCharSpecial: (model) => {
        const value = model[fieldName];
        return !value || isValidUsernameLength(value);
      },
      charTypeRegexes: (model) => {
        const value = model[fieldName];
        return !value || isValidUsernameCharacterTypes(value.slice(1));
      },
      charNotStartSpecial: (model) => {
        const value = model[fieldName];
        return !value || isValidUsernameNotStartSpecial(value);
      },
    },
    errorMessages: {
      stringLength: PEOPLE_CREATEUSER_MODAL_MESSAGES.USERNAME_LENGTH,
      stringLengthAndCharSpecial: PEOPLE_CREATEUSER_MODAL_MESSAGES.USERNAME_LETTER_NUMBER,
      charTypeRegexes: PEOPLE_CREATEUSER_MODAL_MESSAGES.USERNAME_LETTER_NUMBER,
      charNotStartSpecial: PEOPLE_CREATEUSER_MODAL_MESSAGES.USERNAME_NOT_SPECIAL_CHARACTER,
    },
  };
};

export const getPasswordValidation = (fieldName = 'password') => {
  const { appContext } = flex.getStore();
  const passwordConfig = appContext.app.passwordConfig;

  return {
    validators: {
      minLen: (model) => {
        const value = model[fieldName];

        return !value || isValidPasswordMinLength(value, passwordConfig.minimumPasswordLength);
      },
      charTypeRegexes: (model) => {
        const value = model[fieldName];

        return !value || isValidPasswordCharacterTypes(value, passwordConfig.minimumCharacterTypes);
      },
      charTypeNotSpace: (model) => {
        const value = model[fieldName];

        return !value || isValidPasswordNotSpaceCharacter(value);
      },
    },
    errorMessages: {
      minLen: `Password must be at least ${passwordConfig.minimumPasswordLength} characters`,
      charTypeRegexes: `Password must include UPPER and lower case, at least 1 number and 1 special character`,
      charTypeNotSpace: INVALID_PASSWORD_CANNOT_CONTAIN_SPACE_TEXT,
    },
  };
};

export const getPhoneValidation = (fieldName = 'phone', isRequired = true) => {
  const isAMSOrchestrationEnabled = flex.isFeatureToggleEnabled(
    FEATURE.ENABLE_AMS_USER_ORCHESTRATION
  );

  if (!isAMSOrchestrationEnabled) {
    return {
      validations: { matchRegexp: /^[0-9]{3}[-][0-9]{3}[-][0-9]{4}$/ },
      validationErrors: { matchRegexp: CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_NAT_NUMBER_ERR },
    };
  }

  const isEmptyOrWhiteSpace  = (input) => !input || !input.trim();
  const isEmptyValid = (model) => isRequired  ? false : isEmptyOrWhiteSpace(model[fieldName]);

  return {
    validations: {
      isValidPhoneNumberNationalPhoneNumber: model =>
        isEmptyValid(model) || (model[fieldName]?.startsWith('+') || isValidPhoneNumber(model[fieldName] ?? '', 'US')),
      isValidInternationalPhoneNumber: model =>
        isEmptyValid(model) || (!model[fieldName]?.startsWith('+') || isValidPhoneNumber(model[fieldName] ?? '', 'US')),
    },
    validationErrors: {
      isValidPhoneNumberNationalPhoneNumber:
        CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_NAT_NUMBER_ERR,
      isValidInternationalPhoneNumber: CREATE_MANHEIM_ACCOUNT.PHONE_FIELD_INVALID_INT_NUMBER_ERR,
    },
  };
};

function isValidUsernameLength(username) {
  return username.length >= 4 && username.length <= 30;
}

function isValidUsernameCharacterTypes(username) {
  return new RegExp(CHECK_CHARACTER_VALIDATION.REG_EX).test(username);
}

function isValidUsernameNotStartSpecial(username) {
  return new RegExp(CHECK_NOT_START_SPECIAL_CHARACTER_VALIDATION.REG_EX).test(username);
}

function isValidPasswordMinLength(password, minLen) {
  return password.length >= minLen;
}

function isValidPasswordNotSpaceCharacter(password) {
  return new RegExp(NO_WHITE_SPACE_CODE_VALIDATION.REG_EX).test(password);
}

function isValidPasswordCharacterTypes(password, minimumCharTypesCount) {
  //Regex for upper case and lower case letters
  const letters = /([A-Z].*[a-z]|[a-z].*[A-Z])/;
  //Regex for digits
  const digits = /[0-9]/;
  //Regex for nonalphanumeric within ascii range (00 - 127)
  const nonalphaNumeric = /(?=\W)(?=[\x00-\x7F])/;
  //Regex for unicode characters outside of ascii (00 - 127)
  const unicode = /[^\x00-\x7F]/;

  const characterTypeRegexes = [letters, digits, nonalphaNumeric, unicode];

  let characterTypeCount = 0;
  for (let i = 0; i < characterTypeRegexes.length; i++) {
    if (characterTypeRegexes[i].test(password)) {
      characterTypeCount++;
    }
  }

  return characterTypeCount >= Math.min(minimumCharTypesCount, characterTypeRegexes.length);
}

export const notEqualsIgnoreCase = (first, second) => {
  return !first || !second || first.toLowerCase() !== second.toLowerCase();
};

export const handleNotSpaceUserName = (value) => {
  return new RegExp(NO_WHITE_SPACE_CODE_VALIDATION.REG_EX).test(value);
};

export const handleIsSpecialWordsUserName = (value) => {
  return new RegExp(CHECK_SPECIAL_WORDS_CODE_VALIDATION.REG_EX).test(value);
};

export const emailValidators = {
  matchRegexp: EMAIL_VALIDATION.REG_EX,
  isEmail: true,
};

export const isEmailValid = (email) => {
  if (!email) {
    return false;
  }

  return EMAIL_VALIDATION.REG_EX.test(email.trim());
};

export const parsePhone = (phone) => {
  const formattedPhone = getFormattedPhone(phone);

  return formattedPhone;
};

export const getFormattedPhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return phone;
  }

  if (isValidPhoneNumber(phone, DEFAULT_PHONE_COUNTRY)) {
    const isAMSOrchestrationEnabled = flex.isFeatureToggleEnabled(
      FEATURE.ENABLE_AMS_USER_ORCHESTRATION
    );

    if(isAMSOrchestrationEnabled) {
      const phoneNumber = parsePhoneNumber(phone, DEFAULT_PHONE_COUNTRY);

      const getFormattedNationalPhone = () => {
        const national = phoneNumber?.nationalNumber;

        if (national.length === 10) {
          return national.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        }

        return phoneNumber?.formatNational({ nationalPrefix: false });
      }

      if (phoneNumber.countryCallingCode === '1') {
        const formattedNational = getFormattedNationalPhone();

        return `+${phoneNumber.countryCallingCode} ${formattedNational}`;
      }

      return phoneNumber.formatInternational();
    }
  }

  return phone;
};
